import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 15000,
});

const RETRY_DELAYS = [600, 1500];
const RETRYABLE_STATUS = [502, 503, 504];
export const NETWORK_ERROR_MSG = "Connexion lente ou interrompue. Vérifiez votre réseau puis réessayez.";
export const SERVER_ERROR_MSG = "Le service est momentanément indisponible. Réessayez dans quelques instants.";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const isSafeToRetry = (cfg) => cfg.method === "get" || Boolean(cfg.headers?.["Idempotency-Key"]);

export const newIdempotencyKey = () =>
  (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);

// Nouvel essai automatique (réseau coupé, délai dépassé, 502/503/504) puis message clair en français
api.interceptors.response.use(undefined, async (error) => {
  const cfg = error.config;
  const status = error.response?.status;
  const transient = !error.response || RETRYABLE_STATUS.includes(status);
  if (cfg && transient && isSafeToRetry(cfg)) {
    cfg.__retry = cfg.__retry || 0;
    if (cfg.__retry < RETRY_DELAYS.length) {
      await sleep(RETRY_DELAYS[cfg.__retry]);
      cfg.__retry += 1;
      return api(cfg);
    }
  }
  if (!error.response) error.response = { status: 0, data: { detail: NETWORK_ERROR_MSG } };
  else if (status >= 500 && !error.response.data?.detail) error.response.data = { detail: SERVER_ERROR_MSG };
  return Promise.reject(error);
});

export default api;

export function formatApiError(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const FIXED_PRICE = 10000;
export const fcfa = (n) => `${String(n ?? 0).replace(/\B(?=(\d{3})+(?!\d))/g, " ")} FCFA`;

export const vehicleOf = (t) => t?.vehicle_label || [t?.vehicle?.brand, t?.vehicle?.model].filter(Boolean).join(" ") || t?.driver?.vehicle || "";

export function tripStatus(seatsAvailable) {
  if (seatsAvailable === 0) return { key: "complet", label: "Complet", color: "#D94E4E", bg: "#D94E4E14" };
  if (seatsAvailable <= 2) return { key: "limited", label: "Places limitées", color: "#C85A17", bg: "#C85A1714" };
  return { key: "available", label: "Disponible", color: "#2D7A5D", bg: "#2D7A5D14" };
}

export function baggageLabel(hasBaggage) {
  if (hasBaggage === true) return "Oui";
  if (hasBaggage === false) return "Non";
  return "—";
}

export function bookingStatus(status) {
  switch (status) {
    case "confirmed":
      return { label: "Confirmée", color: "#2D7A5D", bg: "#2D7A5D14" };
    case "completed":
      return { label: "Terminée", color: "#5C6B64", bg: "#5C6B6414" };
    case "cancelled":
      return { label: "Annulée", color: "#5C6B64", bg: "#5C6B6414" };
    case "expired":
      return { label: "Expirée", color: "#5C6B64", bg: "#5C6B6414" };
    case "rejected":
      return { label: "Refusée", color: "#D94E4E", bg: "#D94E4E14" };
    case "awaiting_driver":
      return { label: "En attente du chauffeur", color: "#C85A17", bg: "#C85A1714" };
    case "trip_full":
      return { label: "Trajet complet", color: "#D94E4E", bg: "#D94E4E14" };
    case "cancelled_by_driver":
      return { label: "Annulée par le chauffeur", color: "#D94E4E", bg: "#D94E4E14" };
    case "pending":
    default:
      return { label: "En attente", color: "#C85A17", bg: "#C85A1714" };
  }
}

export function isTripPast(trip) {
  return Boolean(trip?.time) && new Date(trip.time).getTime() < Date.now();
}

/* Statut affiché : un trajet confirmé dont la date est passée devient « Terminée ». */
export function displayBookingStatus(b) {
  const s = b.status || "pending";
  if ((s === "confirmed" || s === "pending" || s === "awaiting_driver") && isTripPast(b.trip)) return s === "confirmed" ? "completed" : "expired";
  return s;
}

export const bookingRef = (id) => `AD-${String(id || "").slice(0, 6).toUpperCase()}`;

export const BAGGAGE_NOTE = "Bagages : le prix dépend du volume, à convenir avec le chauffeur.";

export const WHATSAPP_NUMBER = "221772355048";

export const whatsAppLink = (message, number = WHATSAPP_NUMBER) =>
  `https://wa.me/${String(number).replace(/[^0-9]/g, "")}?text=${encodeURIComponent(String(message).normalize("NFC"))}`;

export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  }
}

export function openWhatsApp(message, number = WHATSAPP_NUMBER) {
  const encoded = encodeURIComponent(message);
  const url = `https://wa.me/${String(number).replace(/[^0-9]/g, "")}?text=${encoded}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

export function formatTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      timeZone: "UTC", // Senegal time = UTC, display always in UTC regardless of browser
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function formatDateTimeLocal(iso) {
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    // Use UTC components so the value reflects Senegal time, not browser-local time
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  } catch {
    return "";
  }
}
